import React, {FC, useCallback, useEffect, useMemo} from "react";
import {
    Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Toolbar, Typography, Checkbox,
    Button
} from "@mui/material";
import {useNavigate} from "react-router";
import LogListPagination from "./LogListPagination.tsx";
import ThicknessClassName from "../../thicknessClass/ThicknessClassDetail/ThicknessClassName";
import TimberClassName from "../../timberClass/TimberClassDetail/TimberClassName";
import TimberTypeName from "../../timberType/TimberTypeDetail/TimberTypeName";
import {AsyncFormSelect, usePagination} from '../../../../share';
import {Shipment} from "../../../types/models/Shipment.ts";
import LogDetail from "./LogDetail.tsx";
import {Log, PrimaryLog, toString as toStringLog, UpdateEntityLog} from "../../../../timber/types/models/Log";
import {FormProvider, useForm} from "react-hook-form";
import LogListPdfDialog from "./LogListPdfDialog.tsx";
import {cuttingStateEditCheck, shippingStateCheck, shippingStateEditCheck} from "../../../stateChecks.ts";
import {fixedFormat} from "../../../../utils/numberFormat.ts";
import LogName, {LogNameDisplay} from "../../log/LogDetail/LogName.tsx";
import useSearchDebounce from "../../../../hooks/useSearchDebounce.ts";
import {styled} from "@mui/material/styles";
import {useGetLogPresentationQuery, useUpdateLogMutation, useUpdateMultipleLogMutation, useGetLogTotalQuery} from "../../../apis/Log.api.ts";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import * as crudgen from "../../../../share";
import {useStateSession} from "../../../../hooks/useAuth.ts";
import {useGetCuttingQuery} from "../../../apis/Cutting.api.ts";
import {PrimaryCutting} from "../../../types/models/Cutting.ts";
import Refresh from "@mui/icons-material/Refresh";
import GradientCircularProgress from "../../../../share/components/progress/GradientCircularProgress.tsx";
import {PrimaryCordwood, UpdateEntityCordwood} from "../../../types/models/Cordwood.ts";

const SmallTableHeaderRow = styled(TableRow)(({theme}) => ({
    '&.MuiTableRow-root': {
        [theme.breakpoints.up('md')]: {
            display: 'none',
        },
    },
}));

const SmallTableRow = styled(TableRow)(({theme}) => ({
    '&.MuiTableRow-root': {
        cursor: "pointer",
        [theme.breakpoints.up('md')]: {
            display: 'none',
        },
    },
}));

const BigTableHeaderRow = styled(TableRow)(({theme}) => ({
    '&.MuiTableRow-root': {
        [theme.breakpoints.down('md')]: {
            display: 'none',
        },
    },
}));

const BigTableRow = styled(TableRow)(({theme}) => ({
    '&.MuiTableRow-root': {
        cursor: "pointer",
        [theme.breakpoints.down('md')]: {
            display: 'none',
        },
    },
}));

type IFormSelectProps = {
    shipment: Shipment;
}

const LogListItemForShipment: FC<IFormSelectProps> = ({shipment}) => {

    const session = useStateSession()
    const navigate = useNavigate()
    const { page, pageSize, handleChangePage, handleChangePageSize } = usePagination('ShipmentLog')
    const [log, setLog] = React.useState<Log | undefined>(undefined);
    const [allCheckbox, setAllCheckbox] = React.useState<boolean>(true);

    const methods = useForm();
    
    const {
        data: list = {
            items: [],
            totalItems: 0,
            page: 0,
            pageSize: 10,
            numPages: 0
        },
        isLoading: getLogListLoading,
        isFetching: getLogListFetching,
        refetch: getLogListRefetch,
    } = useGetLogPresentationQuery({
        list: {
            page,
            pageSize,
        },
        filter: {
            shipmentIdRefs: [shipment.id],
        } 
    })
    
    const {
        data: logTotals = [],
        isLoading: getLogTotalLoading,
        refetch: getLogTotalRefetch,
    } = useGetLogTotalQuery({
        shipmentId: shipment?.id,
    }, {skip: !shipment?.id})

    const INIT = {
        totalCommitedCount: 0,
        totalCount: 0,
        totalCommitedVolume: 0,
        totalVolume: 0,
    };

    const getTotals = (totals) => {
        if (totals?.length === 1) {
            return {
                totalCommitedCount: totals[0].commited ? totals[0].totalCount : 0,
                totalCount: totals[0].totalCount,
                totalCommitedVolume: totals[0].commited ? totals[0].totalVolume : 0,
                totalVolume: totals[0].totalVolume,
            };
        } else if (totals?.length === 2) {
            const commitedIndex = totals[0].commited ? 0 : 1;
            const notCommitedIndex = 1 - commitedIndex;
            return {
                totalCommitedCount: totals[commitedIndex].totalCount,
                totalCount: totals[0].totalCount + totals[1].totalCount,
                totalCommitedVolume: totals[commitedIndex].totalVolume,
                totalVolume: totals[0].totalVolume + totals[1].totalVolume,
            };
        } else {
            return INIT;
        }
    };

    const [totalsState, setTotalsState] = React.useState(INIT)

    React.useEffect(() => {
        getLogTotalRefetch()
        const state = getTotals(logTotals)
        setTotalsState(state)
    }, [list, logTotals, getLogTotalRefetch])

    const shipmentEditable = shippingStateEditCheck(shipment?.state)

    const [, setLogInputValue, logInputTerm] = useSearchDebounce();
    const { data: logSearchList = {
        items: [],
        totalItems: 0,
        page: 0,
        pageSize: 12,
        numPages: 0
    }, 
        isLoading: logSearchListLoading,
        isFetching: logSearchListFetching,
    } = useGetLogPresentationQuery({
        list: {
            page: 0,
            pageSize: 12,
        },
        filter: {
            internalKey: logInputTerm,
            cuttingIdRefs: [shipment?.cuttingId],
        },
    }, {
        skip: !(shipment && logInputTerm && logInputTerm.length > 0),
    })

    const [updateLogMutation, { isLoading: updateLogLoading }] = useUpdateLogMutation()
    const [updateMultipleLogMutation, { isLoading: updateMultipleLogLoading }] = useUpdateMultipleLogMutation()

    const updateLog = useCallback(async (log: UpdateEntityLog) => {
        const data = await updateLogMutation(log).unwrap();
        if(data != null) {
            //if update success
            // navigate('/Dashboard/reports')
            getLogTotalRefetch()
        }
    }, [updateLogMutation, navigate, getLogTotalRefetch])
    
    // todo check if shipment can be changed
    const logAddHandler = useCallback((log: Log) => {
        if (shipment) {
            // todo check if shipment was already set
            let update: UpdateEntityLog = {
                id: PrimaryLog.fromEntity(log),
                data: {
                    ...log,
                    shipmentId: shipment.id,
                }
            }
            updateLog(update)
            setLog(undefined)
            // methods.setValue('logId', null)
        }
    }, [shipment, updateLog, setLog])

    const logRemove = useCallback((log: Log) => {
        if (shipment) {
            let update: UpdateEntityLog = {
                id: PrimaryLog.fromEntity(log),
                data: {
                    ...log,
                    shipmentId: undefined,
                }
            }
            updateLog(update)
            setLog(undefined)
            // methods.setValue('logId', null)
        }
    }, [shipment, updateLog, setLog])

    const { data: cutting } = useGetCuttingQuery(PrimaryCutting.fromId(shipment?.cuttingId), {skip: !shipment?.cuttingId})

    const handleCommited = useCallback(async (log: Log) => {
        if (shipment) {
            let update: UpdateEntityLog = {
                id: PrimaryLog.fromEntity(log),
                data: {
                    ...log,
                    commited: !log.commited,
                }
            }
            await updateLog(update)
        }
    }, [shipment, updateLog])

    const handleAllCommited = useCallback(async (commited) => {
        if (shipment && list) {
            const updates: UpdateEntityLog[] = list.items.map(log => {
                return {
                    ...log,
                    commited,
                }
            })
            await updateMultipleLogMutation(updates)
            getLogListRefetch()
            getLogTotalRefetch()
        }
    }, [list, shipment, getLogTotalRefetch])

    useEffect(() => {
        let countCommited = 0
        for (const log of list.items) {
            if (log.commited) {
                countCommited++
            }
        }

        if (countCommited > list.items.length / 2) {
            setAllCheckbox(true)
        } else {
            setAllCheckbox(false)
        }
    }, [list, setAllCheckbox])

    const showEdit = session.hasPermission('', 'applications/timber/entities/Shipments/update/view')
        && cuttingStateEditCheck(cutting?.state)
        && shippingStateEditCheck(shipment?.state)
        && (!shipment || shippingStateEditCheck(shipment?.state))
        && shippingStateCheck(session, shipment?.state)

    return useMemo(() => (

    <Box data-testid="Shipment-logs-container">
        <Paper>
            <Toolbar
                sx={ {
                    pl: { sm: 2 },
                    pr: { xs: 1, sm: 1 },
                } }
                data-testid="Shipment-logs-toolbar"
            >
                <Typography
                    sx={ { flex: '1 1 100%' } }
                    variant="h6"
                    component="div"
                >
                    <Trans>Shipment Logs</Trans>
                </Typography>
                <Box display="flex" justifyContent="flex-end">
                    <LogListPdfDialog shipment={shipment} copies={4}/>
                    <Button onClick={getLogListRefetch}>
                        <Refresh />
                    </Button>
                </Box>
            </Toolbar>
            {shipment && !getLogListLoading && totalsState &&
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Table size="small" sx={{ width: 'auto', minWidth: 300 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell></TableCell>
                                <TableCell align="right"><b><Trans>Commited / Total</Trans></b></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell><b>{shipment.state} <Trans>Count</Trans></b></TableCell>
                                <TableCell align="right">{totalsState.totalCommitedCount} / {totalsState.totalCount}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell><b>{shipment.state} <Trans>Volume (m&#xB3;)</Trans></b></TableCell>
                                <TableCell align="right">{fixedFormat(totalsState.totalCommitedVolume)} / {fixedFormat(totalsState.totalVolume)}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </Box>
            }
            <FormProvider {...methods}>
                <AsyncFormSelect
                    name='logId'
                    label={t`Select log for shipment`}
                    options={ logSearchList.items }
                    optionsLoading={ logSearchListLoading || logSearchListFetching }
                    optionLabelMap={ toStringLog }
                    onInputChange={setLogInputValue}
                    onValueChange={setLog}
                    renderOption={(optionProps, option, { inputValue }) => {
                        const { key, ...filteredOptionProps } = optionProps;
                        return (<li key={key} {...filteredOptionProps}><LogNameDisplay data={option}/></li>)
                    } }
                    renderTags={(value: readonly string[], getTagProps) =>
                        value.map((option: any, index: number) => {
                            const { key, ...tagProps } = getTagProps({ index })
                            return (
                                <crudgen.SelectChip key={key} {...tagProps}>
                                    <LogName id={option}/>
                                </crudgen.SelectChip>
                            );
                        })
                    }
                    data-testid="Shipment-log-select"
                />

                {log && (
                    <LogDetail
                        shipment={shipment}
                        data={log!}
                        onAddLog={shipmentEditable && logAddHandler}
                        onRemoveLog={shipmentEditable && logRemove}
                        data-testid="Shipment-log-detail"
                    />
                )}

                { getLogListLoading ?
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <GradientCircularProgress/>
                    </Box>
                    :
                    <Paper>
                    { getLogListFetching && <GradientCircularProgress absolute={true}/> }
                    <TableContainer data-testid="Shipment-logs-table-container">
                        <Table stickyHeader aria-label="simple table" size="small" data-testid="Shipment-logs-table">
                            <TableHead>
                                <SmallTableHeaderRow>
                                    {showEdit &&
                                        <TableCell component="th" align="center">
                                            <Trans>Commit</Trans>
                                            <Checkbox
                                                color="primary"
                                                checked={allCheckbox}
                                                onClick={(e) => handleAllCommited(!allCheckbox)}
                                            />
                                        </TableCell>
                                    }
                                    <TableCell component="th" align="center"><Trans>No</Trans></TableCell>
                                    <TableCell component="th" align="left"><Trans>Timber Key</Trans></TableCell>
                                    <TableCell component="th" align="right"><Trans>Volume (m&#xB3;)</Trans></TableCell>
                                    <TableCell component="th" align="left"><Trans>Timber Type</Trans></TableCell>
                                </SmallTableHeaderRow>
                                <BigTableHeaderRow>
                                    {showEdit &&
                                        <TableCell component="th" align="center">
                                            <Trans>Commit</Trans>
                                            <Checkbox
                                                color="primary"
                                                checked={allCheckbox}
                                                onClick={(e) => handleAllCommited(!allCheckbox)}
                                            />
                                        </TableCell>
                                    }
                                    <TableCell component="th" align="center"><Trans>No</Trans></TableCell>
                                    <TableCell component="th" align="center"><Trans>Timber Key</Trans></TableCell>
                                    <TableCell component="th" align="center"><Trans>Diameter</Trans></TableCell>
                                    <TableCell component="th" align="center"><Trans>Length</Trans></TableCell>
                                    <TableCell component="th" align="center"><Trans>Volume</Trans></TableCell>
                                    <TableCell component="th" align="center"><Trans>Timber Type</Trans></TableCell>
                                    <TableCell component="th" align="center"><Trans>Class</Trans></TableCell>
                                    <TableCell component="th" align="center"><Trans>Thickness Class</Trans></TableCell>
                                </BigTableHeaderRow>
                            </TableHead>
                            <TableBody>
                                { list.items.map((log, index) => (
                                    <React.Fragment key={ PrimaryLog.fromEntity(log).toPath() }>
                                        <SmallTableRow
                                            hover={true}
                                            key={ PrimaryLog.fromEntity(log).toPath() + '-small' }
                                        >
                                            {showEdit && <TableCell scope="row" align="center">
                                                <Checkbox
                                                    color="primary"
                                                    checked={log.commited}
                                                    onClick={(e) => handleCommited(log)}
                                                />
                                            </TableCell> }
                                            <TableCell scope="row" align="center"
                                                       onClick={() => setLog(log)}
                                            >
                                                { (page * pageSize) + index + 1 }.
                                            </TableCell>
                                            <TableCell scope="row"
                                                       onClick={() => setLog(log)}
                                            >
                                                <Typography component="div" sx={ { fontWeight: 'bold', } }>
                                                    { log.timberKey ?
                                                        <>
                                                            { log.timberKey }
                                                        </>
                                                        :
                                                        '-'
                                                    }
                                                </Typography>


                                                <Typography component="div" variant="body1">
                                                    { log.externalKey &&
                                                        <>
                                                            <Trans>External Key</Trans>:&nbsp;
                                                            { log.externalKey }
                                                        </>
                                                    }
                                                </Typography>


                                                <Typography component="div" variant="body1">
                                                    { log.internalKey &&
                                                        <>
                                                            <Trans>IK</Trans>:&nbsp;
                                                            { log.internalKey }
                                                        </>
                                                    }
                                                </Typography>
                                            </TableCell>
                                            <TableCell scope="row" align="right"
                                                       onClick={() => setLog(log)}
                                            >
                                                <Typography component="div" sx={ { fontWeight: 'bold', } }>
                                                    { log.volume ?
                                                        <>
                                                            {fixedFormat(log.volume) }
                                                        </>
                                                        :
                                                        '-'
                                                    }
                                                </Typography>


                                                <Typography component="div" variant="body1">
                                                    { log.diameter &&
                                                        <>
                                                            {(log.diameter) }
                                                        </>
                                                    }
                                                </Typography>


                                                <Typography component="div" variant="body1">
                                                    { log.length &&
                                                        <>
                                                            {(log.length) }
                                                        </>
                                                    }
                                                </Typography>

                                            </TableCell>
                                            <TableCell scope="row" align="left"
                                                       onClick={() => setLog(log)}
                                            >
                                                <Typography component="div" sx={ { fontWeight: 'bold', } }>
                                                    { log.timberTypeId ?
                                                        <>
                                                            <TimberTypeName id={ log.timberTypeId }/>
                                                        </>
                                                        :
                                                        '-'
                                                    }
                                                </Typography>


                                                <Typography component="div" variant="body1">
                                                    { log.classId &&
                                                        <>
                                                            <Trans>C</Trans>:&nbsp;
                                                            <TimberClassName id={ log.classId }/>
                                                        </>
                                                    }
                                                </Typography>


                                                <Typography component="div" variant="body1">
                                                    { log.thicknessClassId &&
                                                        <>
                                                            <Trans>TC</Trans>:&nbsp;
                                                            <ThicknessClassName id={ log.thicknessClassId }/>
                                                        </>
                                                    }
                                                </Typography>
                                            </TableCell>
                                        </SmallTableRow>
                                        <BigTableRow
                                            hover={true}
                                            key={ PrimaryLog.fromEntity(log).toPath() + '-big' }
                                        >
                                            {showEdit && <TableCell scope="row" align="center">
                                                <Checkbox
                                                    color="primary"
                                                    checked={log.commited}
                                                    onClick={(e) => handleCommited(log)}
                                                />
                                            </TableCell> }
                                            <TableCell width="20%" scope="row" align="center"
                                                       onClick={() => setLog(log)}
                                            >
                                                { (page * pageSize) + index + 1 }.
                                            </TableCell>
                                            <TableCell width="20%" scope="row" align="center"
                                                       onClick={() => setLog(log)}
                                            >
                                                { log.internalKey }
                                            </TableCell>
                                            <TableCell width="20%" scope="row" align="right"
                                                       onClick={() => setLog(log)}
                                            >
                                                { log.diameter }
                                            </TableCell>
                                            <TableCell width="20%" scope="row" align="right"
                                                       onClick={() => setLog(log)}
                                            >
                                                { log.length }
                                            </TableCell>
                                            <TableCell width="20%" scope="row" align="right"
                                                       onClick={() => setLog(log)}
                                            >
                                                {fixedFormat(log.volume)}
                                            </TableCell>
                                            <TableCell width="20%" scope="row" align="center"
                                                       onClick={() => setLog(log)}
                                            >
                                                <TimberTypeName id={log.timberTypeId}/>
                                            </TableCell>
                                            <TableCell width="20%" scope="row" align="center"
                                                       onClick={() => setLog(log)}
                                            >
                                                <TimberClassName id={log.classId}/>
                                            </TableCell>
                                            <TableCell width="20%" scope="row" align="center"
                                                       onClick={() => setLog(log)}
                                            >
                                                <ThicknessClassName id={log.thicknessClassId}/>
                                            </TableCell>
                                        </BigTableRow>
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <LogListPagination
                        totalItems={ list.totalItems }
                        page={Number(page)}
                        pageSize={pageSize}
                        handleChangePage={handleChangePage}
                        handleChangePageSize={handleChangePageSize}
                    />
                </Paper>
                }
            </FormProvider>
        </Paper>
    </Box>
    ), [logSearchListLoading, logSearchListFetching, logSearchList, log, setLog, logInputTerm, setLogInputValue,
        list, page, pageSize, handleChangePage, handleChangePageSize, logAddHandler, handleCommited, showEdit, allCheckbox,
        getLogListFetching, getLogListLoading])
}

export default LogListItemForShipment;
